import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Container, Typography, Paper, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow, Button, TextField, 
  Dialog, DialogActions, DialogContent, DialogTitle, 
  IconButton, Box, Alert, Snackbar, TablePagination
} from '@mui/material';
import { useParams } from "react-router-dom";
import { Add as AddIcon, Visibility as VisibilityIcon , Edit as EditIcon, HourglassTop as HourglassTopIcon, SaveAs as SaveAsIcon, Close as CloseIcon } from '@mui/icons-material';
import Header from "../../components/common/header/Header";
import Footer from "../../components/common/footer/Footer";
import { getApiCall, postApiCall } from '../../utils/Apicalls';
import './TennantUsersPage.css';

function TennantUsersPage() {
  const navigate = useNavigate();
  const [parentUserId, setParentUserId] = useState("");
  const [users, setUsers] = useState([]);
  const [userDetail, setUserDetail] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [openMinutesDialog, setOpenMinutesDialog] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    email: '',
    password: '',
    minutes: 1,
    role_id: 'b2b'
  });
  const [minutesToAdd, setMinutesToAdd] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [page, setPage] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [roleList, setRoleList] = useState([]);

  const [openMinutesHistoryDialog, setOpenMinutesHistoryDialog] = useState(false);
  const [minuteListLoading, setMinuteListLoading] = useState(false);
  const [minuteList, setMinuteList] = useState([]);
  const [usedMinutes, setUsedMinutes] = useState(0);
  const [remainingMinutes, setRemainingMinutes] = useState(0);

  const itemId = useParams();

  useEffect(() => {
    if (itemId?.id) {
      setParentUserId(itemId.id);
    }
  }, []);

  // Check if user is admin and fetch users
  useEffect(() => {
    const checkAdminAndFetchUsers = async () => {
      const userDetails = localStorage.getItem("userDetails");
      if (!userDetails) {
        navigate('/login');
        return;
      }

      if (itemId?.id) {
        setParentUserId(itemId.id);
      }

      try {
        const userData = JSON.parse(userDetails);
        if (userDetails) {
          setUserDetail(userData);
        }
        if (['admin', 'sales', 'tennant'].indexOf(userData.role_id) === -1) {
          navigate('/home');
          return;
        }

        // User is admin, fetch users
        fetchUsers({
          page: 0,
          limit: rowsPerPage
        });
      } catch (error) {
        console.error("Error checking admin status:", error);
        navigate('/login');
      }
    };

    checkAdminAndFetchUsers();
  }, [navigate]);

  const fetchUsers = async (pageConfig) => {
    setLoading(true);
    setTotalUsers(0);
    setUsers([]);
    setError('');
    try {
      let query = `page=${pageConfig.page + 1}&limit=${pageConfig.limit}&roles=b2b&parentUserId=${itemId?.id || ''}`;
      const result = await getApiCall('interview-app/users/list?' + query);
      if (result?.status) {
        if (result?.result?.length > 0) {
          setUsers(result?.result);
        }
        setRemainingMinutes(result?.remainingMinutes || 0);
        setTotalUsers(parseInt(result?.totalRecords || 0));
      } else {
        setError('Failed to fetch users');
      }
    } catch (err) {
      console.error("Error fetching users:", err);
      setError('An error occurred while fetching users');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setFormData({
        firstName: user.first_name || '',
        email: user.email || '',
        password: '',
        minutes: 0,
        role_id: user.role_id || 'b2b'
      });
      setCurrentUser(user);
    } else {
      setFormData({
        firstName: '',
        email: '',
        password: '',
        minutes: 1,
        role_id: 'b2b'
      });
      setCurrentUser(null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleOpenViewDialog = (user) => {
    setCurrentUser(user);
    setMinutesToAdd('');
    setOpenMinutesDialog(true);
  };

  const handleCloseMinutesDialog = () => {
    setOpenMinutesDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async () => {
    try {
      let result;

      if (!formData?.firstName) {
        setSnackbar({
          open: true,
          message: 'Please enter first name',
          severity: 'error'
        });
        return;
      }

      if (!formData?.firstName) {
        setSnackbar({
          open: true,
          message: 'Please enter first name',
          severity: 'error'
        });
        return;
      }
      if (!formData?.email) {
        setSnackbar({
          open: true,
          message: 'Please enter username',
          severity: 'error'
        });
        return;
      }
      if (!formData?.password) {
        setSnackbar({
          open: true,
          message: 'Please enter password',
          severity: 'error'
        });
        return;
      }
      
      if (currentUser) {
        let paramBody = {
          firstName: formData.firstName,
          email: formData.email,
          b2bPassword: formData.password,
        };

        result = await postApiCall(`interview-app/users/edit/${currentUser.id}`, paramBody);
      } else {
        if (remainingMinutes < parseInt(formData.minutes)) {
          setSnackbar({
            open: true,
            message: remainingMinutes ? `You have ${remainingMinutes} minutes remaining only` : 'You have no minutes remaining',
            severity: 'error'
          });
          return;
        }

        let paramBody = {
          firstName: formData.firstName,
          lastName: "",
          email: formData.email,
          password: formData.password,
          minutes: formData.minutes,
          roleType: formData.role_id,
          parentUserId: parentUserId
        };
        result = await postApiCall('interview-app/users/add', paramBody);
      }
      
      if (result?.status) {
        setSnackbar({
          open: true,
          message: currentUser ? 'User updated successfully' : 'User created successfully',
          severity: 'success'
        });
        fetchUsers({
          page: 0,
          limit: rowsPerPage
        });
        handleCloseDialog();
      } else {
        setSnackbar({
          open: true,
          message: result?.message || 'Operation failed',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setSnackbar({
        open: true,
        message: 'An error occurred',
        severity: 'error'
      });
    }
  };

  const handleAddMinutes = async () => {
    if (!currentUser || !minutesToAdd || isNaN(minutesToAdd) || parseInt(minutesToAdd) <= 0) {
      setSnackbar({
        open: true,
        message: 'Please enter a valid number of minutes',
        severity: 'error'
      });
      return;
    }

    if (remainingMinutes < parseInt(minutesToAdd)) {
      setSnackbar({
        open: true,
        message: remainingMinutes ? `You have ${remainingMinutes} minutes remaining only` : 'You have no minutes remaining',
        severity: 'error'
      });
      return;
    }

    try {
      const result = await postApiCall(`interview-app/purchaseplan/addminutes`, {
        minutes: parseInt(minutesToAdd),
        userId: currentUser.id
      });
      
      if (result?.status) {
        setSnackbar({
          open: true,
          message: `Successfully added ${minutesToAdd} minutes to ${currentUser.first_name}`,
          severity: 'success'
        });
        getMinutesHistory(currentUser);
        fetchUsers({
          page: 0,
          limit: rowsPerPage
        }, userDetail);
        handleCloseMinutesDialog();
      } else {
        setSnackbar({
          open: true,
          message: result?.message || 'Failed to add minutes',
          severity: 'error'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'An error occurred while adding minutes',
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchUsers({
      page: newPage,
      limit: rowsPerPage
    });
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(event.target.value);
    setPage(0);
    fetchUsers({
      page: 0,
      limit: event.target.value
    });
  };

  const handleOpenMinutesHistoryDialog = async (user) => {
    setCurrentUser(user);
    setMinutesToAdd('');
    setMinuteList([]);
    setMinuteListLoading(true);
    setOpenMinutesHistoryDialog(true); 
    getMinutesHistory(user);
  };

  const getMinutesHistory = async (user) => {
    try {
      const result = await getApiCall(`interview-app/interview/history/${user.id}/webApp`);
      if (result?.status) {
        if (result?.result?.list?.length > 0) {
          setMinuteList(result.result.list.map((m) => {
            m.editable = false;
            return m;
          }));
        }
        setUsedMinutes(result?.result?.usedMinutes || 0);
      } else {
        setError('Failed to fetch minutes list');
      }
    } catch (err) {
      setError('An error occurred while fetching minutes list');
    } finally {
      setMinuteListLoading(false);
    }
  };

  const handleCloseMinutesHistoryDialog = () => {
    setOpenMinutesHistoryDialog(false);
  };

  const saveMinutesUpdate = async (minuteHistory) => {
    let purchase_minutes = parseInt(minuteHistory.purchase_minutes);
    let oldPurchaseMinutes = parseInt(minuteHistory.oldPurchaseMinutes);

    if (!(purchase_minutes >= 0 && purchase_minutes >= minuteHistory.remainingMinutes)) {
      setSnackbar({
        open: true,
        message: `Please enter a valid number of minutes, but not more than remaining minutes(${minuteHistory.remainingMinutes}) available`,
        severity: 'error'
      });
      return;
    }
    if (remainingMinutes < (purchase_minutes - oldPurchaseMinutes)) {
      setSnackbar({
        open: true,
        message: remainingMinutes ? `You have ${remainingMinutes} minutes remaining only, so you add maximum ${oldPurchaseMinutes + remainingMinutes} minutes.` : 'You have no minutes remaining',
        severity: 'error'
      });
      return;
    }
    let url = 'interview-app/purchaseplan/updateminutes/' + minuteHistory.id;
    let paramBody = {
      minutes: purchase_minutes
    };

    let result = await postApiCall(url, paramBody);
    if (result?.status) {
      setSnackbar({
        open: true,
        message: 'Minutes updated successfully',
        severity: 'success'
      });
      minuteHistory.editable = false;
      setMinuteList([...minuteList]);
      fetchUsers({
        page: 0,
        limit: rowsPerPage
      }, userDetail);
    }
  };

  const handleOpenMinutesDialog = () => {
    setOpenMinutesDialog(true);
    setMinutesToAdd(0);
  };
 
  return (
    <div className="admin-page">
      <Header />
      <Container className="admin-container">
        <Box className="admin-header">
          <Typography variant="h4" component="h1">
            Tennant Users ({remainingMinutes || 0} minutes remaining)
          </Typography>
          <div>
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              styles={{marginRight: 10}}
            >
              Add User
            </Button>
            {userDetail?.role_id !== "tennant" && (
              <Button 
                variant="contained" 
                color="secondary" 
                onClick={() => {
                  navigate('/admin');
                }}
              >
                Back
              </Button>
            )}
          </div>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Paper className="users-table-container">
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Username</TableCell>
                  <TableCell>Password</TableCell>
                  <TableCell>Remaining Minutes</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No users found
                    </TableCell>
                  </TableRow>
                ) : (
                  users
                    .map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>{user.first_name} {user.last_name}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{user.b2b_password || '-'}</TableCell>
                        <TableCell>{user.remainingMinutes > 0 ? `${user.remainingMinutes} minutes` : '0 minutes'}</TableCell>
                        <TableCell>
                          <Box className="action-buttons">
                            <IconButton 
                              color="primary" 
                              onClick={() => handleOpenMinutesHistoryDialog(user)}
                              title="Manage Minutes"
                            >
                              <HourglassTopIcon />
                            </IconButton>
                            {user.role_id === 'tennant' && (
                              <IconButton 
                                color="primary" 
                                onClick={() => handleOpenViewDialog(user)}
                                title="View tennant users"
                              >
                                <VisibilityIcon />
                              </IconButton>
                            )}
                            <IconButton 
                              color="secondary" 
                              onClick={() => handleOpenDialog(user)}
                              title="Edit User"
                            >
                              <EditIcon />
                            </IconButton>
                            {/* <IconButton 
                              color="error" 
                              onClick={() => handleDeleteUser(user._id)}
                              title="Delete User"
                            >
                              <DeleteIcon />
                            </IconButton> */}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalUsers}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </Container>

      {/* User Form Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <div className='row'>
            <div className='col-7'>
              {currentUser ? 'Edit User' : 'Add New User'}
            </div>
            <div className='col-5 text-end'>
              <button 
                className="exit-button" 
                onClick={handleCloseDialog} 
                title="Exit"
              >
                <CloseIcon />
              </button>
            </div>
          </div>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="firstName"
            label="Name"
            type="text"
            fullWidth
            value={formData.firstName}
            onChange={handleInputChange}
          />
          {!currentUser && (
            <TextField
              margin="dense"
              name="email"
              label="Username"
              type="text"
              fullWidth
              value={formData.email}
              onChange={handleInputChange}
            />
          )}
          <TextField
            margin="dense"
            name="password"
            label="Password"
            type="text"
            fullWidth
            value={formData.password}
            onChange={handleInputChange}
          />
          {!currentUser && (
            <TextField
              margin="dense"
              name="minutes"
              label="Minutes"
              type="number"
              fullWidth
              value={formData.minutes}
              onChange={handleInputChange}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {currentUser ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* minutes history Dialog */}
      <Dialog open={openMinutesHistoryDialog} onClose={handleCloseMinutesHistoryDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <div className='row'>
            <div className='col-6'>Manage Minutes</div>
            <div className='col-5 text-end'>
              <Button 
                variant="contained" 
                color="primary" 
                startIcon={<AddIcon />}
                onClick={() => handleOpenMinutesDialog()}
                styles={{marginRight: 10, right: 50}}
              >
                Add Minutes
              </Button>
            </div>
            <div className='col-1 text-end'>
              <button 
                className="exit-button" 
                onClick={handleCloseMinutesHistoryDialog} 
                title="Exit"
              >
                <CloseIcon />
              </button>
            </div>
          </div>
        </DialogTitle>
        <DialogContent>
          <Paper className="users-table-container">
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Plan Name</TableCell>
                    <TableCell>Purchased Minutes</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {minuteListLoading ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : minuteList.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No history found
                      </TableCell>
                    </TableRow>
                  ) : (
                    minuteList
                      .map((minuteHistory) => (
                        <TableRow key={minuteHistory.id}>
                          <TableCell>{minuteHistory.plan_name}</TableCell>
                          <TableCell>
                            <TextField
                              margin="dense"
                              name="minutes"
                              label="Minutes"
                              type="number"
                              disabled={minuteHistory.editable == false}
                              fullWidth
                              value={minuteHistory.purchase_minutes}
                              onChange={(e) => {
                                minuteHistory.purchase_minutes = e.target.value;
                                setMinuteList([...minuteList]);
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Box className="action-buttons">
                              {minuteHistory?.isRemainingMinutes == true && (
                                <>
                                  {minuteHistory.editable == true ? (
                                    <IconButton 
                                      color="primary" 
                                      onClick={() => saveMinutesUpdate(minuteHistory)}
                                      title="Save Minutes"
                                    >
                                      <SaveAsIcon />
                                    </IconButton>
                                  ) : (
                                    <IconButton 
                                      color="primary" 
                                      onClick={() => {
                                        minuteHistory.editable = true;
                                        minuteHistory.oldPurchaseMinutes = minuteHistory.purchase_minutes;
                                        setMinuteList([...minuteList]);
                                      }}
                                      title="Edit Minutes"
                                    >
                                      <EditIcon />
                                    </IconButton>
                                  )}
                                </>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </DialogContent>
      </Dialog>

      {/* Add minutes Dialog */}
      <Dialog open={openMinutesDialog} onClose={handleCloseMinutesDialog} maxWidth="xs" fullWidth>
        <DialogTitle>
          <div className='row'>
            <div className='col-7'>Add Minutes</div>
            <div className='col-5 text-end'>
              <button 
                className="exit-button" 
                onClick={handleCloseMinutesDialog} 
                title="Exit"
              >
                <CloseIcon />
              </button>
            </div>
          </div>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Adding Minutes for: {currentUser?.firstName}
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Minutes to Add"
            type="number"
            fullWidth
            value={minutesToAdd}
            onChange={(e) => setMinutesToAdd(e.target.value)}
            inputProps={{ min: "1", step: "1" }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMinutesDialog}>Cancel</Button>
          <Button onClick={handleAddMinutes} variant="contained" color="primary">
            Add Minutes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default TennantUsersPage;
